import React from 'react';
import Icon from '../../../components/AppIcon';

const IndependenceSection = () => {
  const independenceFramework = [
    {
      title: "Conflict Assessment Protocol",
      description: "Comprehensive conflict checking system ensuring independence from all parties, related entities, and potential beneficiaries.",
      features: [
        "Multi-database conflict screening",
        "Ongoing monitoring throughout engagement",
        "Disclosure of any potential conflicts"
      ],
      icon: "Shield"
    },
    {
      title: "Ethical Standards Compliance",
      description: "Adherence to professional valuation standards, court rules, and ethical guidelines governing expert witness testimony.",
      features: [
        "NACVA and professional standards compliance",
        "Court-specific rule adherence",
        "Continuous professional development"
      ],
      icon: "Scale"
    },
    {
      title: "Independence Documentation",
      description: "Formal independence letters and declarations required for court acceptance and professional credibility.",
      features: [
        "Independence affidavits",
        "Professional liability coverage",
        "Engagement letter clarity"
      ],
      icon: "FileCheck"
    }
  ];

  const ethicalConsiderations = [
    {
      principle: "Objectivity",
      description: "Unbiased analysis based solely on facts, data, and accepted valuation methodology",
      commitment: "No predetermined conclusions or advocacy positions"
    },
    {
      principle: "Independence",
      description: "Freedom from conflicts of interest that could compromise professional judgment",
      commitment: "Comprehensive conflict screening and ongoing monitoring"
    },
    {
      principle: "Competence",
      description: "Appropriate qualifications, experience, and knowledge for the specific valuation matter",
      commitment: "Continuous education and specialization maintenance"
    },
    {
      principle: "Integrity",
      description: "Honest and transparent communication of methodology, assumptions, and limitations",
      commitment: "Full disclosure of all material factors affecting valuation"
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-700 rounded-full px-4 py-2 text-sm font-medium mb-6">
            <Icon name="Shield" size={16} className="mr-2" />
            Independence & Ethics
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Uncompromised Professional Independence
          </h2>
          
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our independence framework ensures court acceptance and professional credibility through rigorous ethical standards and conflict management protocols.
          </p>
        </div>

        {/* Independence Framework */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {independenceFramework?.map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-border hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Icon name={item?.icon} size={24} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-primary">
                  {item?.title}
                </h3>
              </div>

              <p className="text-text-secondary mb-6 leading-relaxed">
                {item?.description}
              </p>

              <div className="space-y-3">
                {item?.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Ethical Principles */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-border">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-primary mb-4">
              Core Ethical Principles
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Our expert witness services are grounded in fundamental ethical principles that ensure credible, defensible testimony.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {ethicalConsiderations?.map((item, index) => (
              <div key={index} className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                    <span className="text-sky-600 font-bold text-sm">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-primary mb-2">
                      {item?.principle}
                    </h4>
                    <p className="text-text-secondary mb-3 leading-relaxed">
                      {item?.description}
                    </p>
                    <div className="bg-sky-50 border-l-4 border-sky-400 p-4 rounded-r-lg">
                      <p className="text-sm font-medium text-sky-800">
                        Our Commitment: {item?.commitment}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Court Acceptance Statistics */}
        {/* <div className="mt-16 bg-gradient-to-r from-emerald-500 to-sky-500 rounded-2xl p-8 lg:p-12 text-white">
          <div className="text-center mb-8">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Court Acceptance Record
            </h3>
            <p className="text-emerald-100 max-w-2xl mx-auto">
              Our independence framework and ethical standards have resulted in consistent court acceptance across multiple jurisdictions.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold mb-2">100%</div>
              <div className="text-emerald-100">Court Qualification Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold mb-2">95%</div>
              <div className="text-emerald-100">Opinion Acceptance</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold mb-2">15+</div>
              <div className="text-emerald-100">Jurisdictions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold mb-2">0</div>
              <div className="text-emerald-100">Ethical Violations</div>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default IndependenceSection;